import { AppDispatch, AppThunk, RootState } from "./store"
import ChatHelpers from "../lib/chatHelpers"
import { Client, jid, xml } from "@xmpp/client"
import { Element } from "@xmpp/xml"
import { v4 as uuid } from "uuid"
import XmppGroupChatApi from "../api/xmppGroupChatApi"
import { StanzaType } from "../lib/stanzaParser"
import MessageHelpers from "../lib/messageHelpers"
import { turnSent } from "./agentPipelineSlice"
import ChatMarkersApi from "../api/chatMarkersApi"
import MarkerParser from "../lib/markerParser"
import ReactionsParser from "../lib/reactionsParser"
import {
  selectActiveChat,
  selectActiveChatIndex,
  selectIsSlickAISilenced,
} from "./chatsSliceSelectors"
import {
  selectTranslationPair,
  type TranslationPair,
} from "./accountChatsSelectors"
import {
  selectTranslationPaused,
  setTranslationFailure,
} from "./composerTranslationSlice"
import XmppApi from "../api/xmppApi"
import MessageParser from "../lib/messageParser"
import XmppSimsApi from "../api/xmppSimsApi"
import { changeActiveChat, Chat, removeChat, upsertChat } from "./chatsSlice"
import {
  addBatchMessageReactions,
  addMessage,
  AddMessageAction,
  addMessageCorrection,
  markMessage,
  Message,
  MessageDisplayType,
  Reaction,
} from "./messagesSlice"
import { add as addToSendoutQueue } from "./sendoutQueueSlice"
import { selectChatReplyMessage } from "./chatRepliesSliceSelectors"
import { parse as jidParse } from "../lib/cachedJid"
import { JID } from "@xmpp/jid"
import { addToSyncQueue } from "./roomSyncStatusSlice"
import XmppVCardApi from "../api/xmppVCardApi"
import { upsertProfile } from "./profilesSlice"
import {
  selectArchivedChats,
  selectShouldNotify,
} from "./chatsSettingsSelectors"
import { selectSettings } from "./settingsSlice"
import { selectAccountChatByJid } from "./accountChatsSlice"
import { sortChats } from "@/hooks/useSortedChatsByLastEvent"
import { UploadedFile } from "@/hooks/useExtractDataFromOrder"
import { messageAuthorName } from "./profilesSliceSelectors"
import { isDriverRoomGroup } from "../lib/slickAISilence"
import { debounce } from "lodash"
import { getTransicsMessagePresentation } from "../lib/transicsMessagePresentation"
import { DRIVER_AGENT_THREAD, isAgentChatMessage } from "../lib/agentThread"
import { isNotifyOnActiveChatEnabled } from "../lib/notifyOnActiveChat"
import { addBookmark } from "../api/xeps/PepNativeBookmarks"
import { captureError } from "../ErrorHandlers"
import posthog from "posthog-js"
import AdHocCommands, { parseTranslatedText } from "@/api/adHocCommands"
import { ORIGINAL_BODY_NS } from "@/lib/originalBody"

const TRANSLATION_FLAG = "outgoing-message-translation"

/** What the dispatcher typed, when `body` carries a machine translation. */
type OriginalText = { text: string; lang: string }

const originalElement = (original: OriginalText) =>
  xml(
    "original",
    { xmlns: ORIGINAL_BODY_NS, lang: original.lang },
    original.text,
  )

const isMessageFromTranschat = (from: string) => {
  try {
    return jid.parse(from).resource === "transchat"
  } catch {
    return false
  }
}

const isDriverRoom = (roomBareJid: string, state: RootState) =>
  isDriverRoomGroup(selectAccountChatByJid(roomBareJid)(state)?.group)

/**
 * See `lib/slickAISilence.ts` for the rule. The axis is the SENDER, not the
 * room: a driver's message in the same room still notifies. The easiest way to
 * break this is to lift the room check above the sender check.
 */
const isSilencedSlickAIMessage = (message: Message, state: RootState) => {
  if (!ChatHelpers.isSilenceableSlickAIMessage(message)) return false

  return selectIsSlickAISilenced(state)(
    jidParse(message.from).bare().toString(),
  )
}

const playNotificationSound = debounce(
  () => {
    new Audio("/notification.wav").play().catch(() => {})
  },
  2000,
  { leading: true, trailing: false }, // The { leading: true, trailing: false } options ensure the sound plays immediately on the first call, then ignores subsequent calls for 2 seconds.
)

// TODO remove as it is not used in production code - but requires changing syncSingleRoom tests
export const sendChatPresence = (XMPP: Client, requestHistorySince?: Date) => {
  return (_dispatch: AppDispatch, getState: () => RootState) => {
    const state: RootState = getState()
    state.chats.forEach((chat) => {
      if (chat.room && XMPP.jid) {
        XmppGroupChatApi.sendPresence(
          XMPP,
          chat.jid,
          XMPP.jid?.getLocal(),
          requestHistorySince,
        )
      }
    })
  }
}

export const sendFile = (
  file: File,
  url: string,
  client: Client,
  caption?: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const activeChat = selectActiveChat(getState())

    const message = XmppSimsApi.buildMessage(
      client,
      file,
      activeChat.jid,
      url,
      activeChat.room ? "groupchat" : "chat",
      caption,
    )

    sendGroupchatStanza(client, dispatch, message, getState)

    // Like sendMessage: the agent's turn starts when the file goes out. Started
    // any later it can land after the agent has signalled, and the UI then
    // shows a second, phantom turn ticking under the answer.
    if (ChatHelpers.isAIChatRoom(activeChat.jid)) {
      dispatch(turnSent({ roomJid: activeChat.jid, at: Date.now() }))
    }
  }
}

/**
 * Actually call the translation wizard command and interpret its result.
 *
 * The three outcomes are deliberately distinct, because two of them look
 * identical on the wire and must not be confused:
 *  - `translated`: send this instead of what was typed.
 *  - `skipped`: the service decided no translation was needed, or handed
 *    the text back unchanged. Send the original. Nothing went wrong, so say
 *    nothing.
 *  - `failed`: timeout, unreachable, or a malformed reply. Send NOTHING and
 *    let the dispatcher decide — delivering their own language to a driver
 *    who cannot read it, quietly, is the outcome this whole feature exists
 *    to prevent.
 */
type TranslationOutcome =
  | { kind: "translated"; text: string }
  | { kind: "skipped" }
  | { kind: "failed" }

const translateOutgoing = async (
  client: Client,
  text: string,
  pair: TranslationPair,
): Promise<TranslationOutcome> => {
  try {
    const response = await AdHocCommands.translateText({
      client,
      text,
      targetLanguage: pair.target,
      sourceLanguage: pair.source,
    })
    const translated = parseTranslatedText(response)
    // Text handed back unchanged was already in the driver's language (the
    // declared source is only the dispatcher's profile, not a detection).
    // Sending it as a translation would put the same text in both the body
    // and the original, and the bubble would show it twice.
    return translated && translated.trim() !== text.trim()
      ? { kind: "translated", text: translated }
      : { kind: "skipped" }
  } catch (error) {
    return { kind: "failed" }
  }
}

export const sendMessage = (
  text: string,
  client: Client,
  myJid: string,
  activeChat: Chat,
  options: MessageOptions = {},
) => {
  return (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const state = getState()
    const replyMessage = selectChatReplyMessage(activeChat, "driver")(state)

    // Only room messages to drivers are translated. The one-to-one branch
    // below is left exactly as it was. The pair is read first, and
    // short-circuits the feature-flag check, so that callers with nothing to
    // translate never hit an `await` at all — their dispatch stays fully
    // synchronous, exactly like before this feature existed.
    //
    // It is undefined when the dispatcher and the driver speak the same
    // language, which is what keeps those sends on the synchronous path.
    const pair = activeChat.room
      ? selectTranslationPair(activeChat.jid, myJid)(state)
      : undefined
    // Only free text typed in the composer is translated. Transport orders,
    // route summaries and share-route/start-tracking messages come through
    // this same thunk but carry structured payloads and share links
    // (`...<routeToken>`, HereWeGo `?secret=<routeToken>`) that must survive
    // byte-for-byte — an LLM rewriting or truncating a token would hand the
    // driver a dead link, and it is the only copy they get.
    const isStructuredSend =
      !!options.deliveryOrderMetadata ||
      !!options.skipRouteDetection ||
      !!options.startTracking
    // Two ways to opt out, and they are read here rather than in the composer
    // so every send path honours them: `skipTranslation` on the send itself
    // ("send untranslated" after a failure), and the per-chat pause the
    // dispatcher toggles with the flag button.
    const skipTranslation =
      !!options.skipTranslation ||
      selectTranslationPaused(activeChat.jid)(state)
    const shouldTranslate =
      !!pair &&
      !isStructuredSend &&
      !skipTranslation &&
      posthog.isFeatureEnabled(TRANSLATION_FLAG) === true

    const sendGroupOrReply = (bodyText: string, original?: OriginalText) => {
      if (replyMessage) {
        const stanza = buildReplyGroupchatStanza(
          activeChat.jid,
          bodyText,
          replyMessage,
          options,
          original,
        )
        sendGroupchatStanza(client, dispatch, stanza, getState)
        return
      }

      const stanza = buildPlainGroupchatStanza(
        activeChat.jid,
        bodyText,
        options,
        original,
      )
      sendGroupchatStanza(client, dispatch, stanza, getState)
    }

    // The agent's turn starts when the dispatcher sends, not when the agent
    // first replies, so the timer counts what the dispatcher waits.
    if (ChatHelpers.isAIChatRoom(activeChat.jid)) {
      dispatch(turnSent({ roomJid: activeChat.jid, at: Date.now() }))
    }

    if (replyMessage || activeChat.room) {
      if (shouldTranslate) {
        // Returned so the caller can await it: the optimistic local echo
        // only happens once the translation comes back, and `ChatInput`
        // keeps its controls locked until this settles. Overlapping sends to
        // one chat are prevented there rather than ordered here — see
        // `handleOnSend` and the failure-bar buttons, all gated on isSending.
        return (async () => {
          const outcome = await translateOutgoing(client, text, pair!)

          if (outcome.kind === "failed") {
            // Send nothing. The composer raises a bar holding this text and
            // asks the dispatcher to retry or send it untranslated, so the
            // decision to put their own language in front of the driver is
            // always theirs and always deliberate.
            dispatch(
              setTranslationFailure({
                chatJid: activeChat.jid,
                failure: { text, options },
              }),
            )
            return
          }

          if (outcome.kind === "translated") {
            sendGroupOrReply(outcome.text, {
              text,
              // What the dispatcher typed in, from their own preferred
              // language. MAM archives this forever, so it comes from their
              // profile and is never guessed from the browser locale.
              lang: pair!.source,
            })
            return
          }

          // "skipped": the service decided no translation was needed, so the
          // original goes out untouched and the dispatcher is not bothered.
          sendGroupOrReply(text)
        })()
      }

      // Nothing to translate: stays fully synchronous, exactly as before
      // this feature existed.
      sendGroupOrReply(text)
      return Promise.resolve()
    }

    XmppApi.sendMessage(client, activeChat.jid, text).then((message) => {
      const parsedMessage = MessageParser.parse(message, {
        timestamp: new Date().toISOString(),
      })
      if (MessageHelpers.isToMyself(parsedMessage)) return

      dispatch(
        addMessage({
          message: parsedMessage,
          myJid,
        }),
      )
    })
    return Promise.resolve()
  }
}

export const forwardMessage = (
  originalMessage: Message,
  client: Client,
  myJid: string,
  targetChat: Chat,
) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    // Use messageAuthorName selector for better name resolution from profiles/roster/contacts
    const originalAuthor = messageAuthorName(
      originalMessage,
      jidParse(myJid),
    )(state)

    if (targetChat.room) {
      const stanza = buildForwardedGroupchatStanza(
        targetChat.jid,
        originalMessage,
      )
      sendGroupchatStanza(client, dispatch, stanza, getState)
      return
    }
  }
}

export type MessageOptions = {
  /**
   * Send exactly what was typed, without translating it. Set when the
   * dispatcher paused translation for the chat, and when they answer
   * "send untranslated" after a failure.
   */
  skipTranslation?: boolean
  skipRouteDetection?: boolean
  startTracking?: boolean
  deliveryOrderMetadata?: {
    originalMetadata?: any
    files?: UploadedFile[]
    deliveryOrderId?: string
  }
}

export const sendDriverAgentThreadMessage = (
  text: string,
  client: Client,
  activeChat: Chat,
  replaceId?: string,
) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    const messageElements = [
      xml("body", {}, text),
      xml("markable", "urn:xmpp:chat-markers:0"),
      xml("thread", {}, DRIVER_AGENT_THREAD),
      xml("internal", "urn:slickshift:hints:0"),
      xml("agent-chat", "urn:slickshift:hints:0"),
      xml("skip-route-detection", "urn:slickshift:hints:0"),
    ]
    if (replaceId) {
      messageElements.push(
        xml("replace", {
          xmlns: "urn:xmpp:message-correct:0",
          id: replaceId,
        }),
      )
    }
    const stanza = xml(
      "message",
      {
        type: "groupchat",
        to: activeChat.jid,
        from: window.myJID.toString(),
        id: uuid(),
      },
      ...messageElements,
    )
    sendGroupchatStanza(client, dispatch, stanza, getState)
  }
}

const buildPlainGroupchatStanza = (
  to: string,
  text: string,
  options: MessageOptions = {},
  original?: OriginalText,
): Element => {
  const messageElements = [
    xml("body", {}, text),
    xml("markable", "urn:xmpp:chat-markers:0"),
  ]
  if (original) {
    messageElements.push(originalElement(original))
  }
  if (options.skipRouteDetection) {
    messageElements.push(xml("skip-route-detection", "urn:slickshift:hints:0"))
  }
  if (options.startTracking) {
    window.isRouteStartTrackingCountdownActive = true
    document.dispatchEvent(new CustomEvent("start-tracking"))
  }
  if (options.deliveryOrderMetadata) {
    const { originalMetadata, files, deliveryOrderId } =
      options.deliveryOrderMetadata
    const deliveryOrderChildren = []

    // Add the original metadata if available
    if (originalMetadata) {
      deliveryOrderChildren.push(
        xml(
          "metadata",
          {},
          xml("json", "urn:xmpp:json:0", JSON.stringify(originalMetadata)),
        ),
      )
    }
    if (files) {
      for (const file of files) {
        if (file.url) {
          deliveryOrderChildren.push(xml("file-url", {}, file.url))
        }

        if (file) {
          deliveryOrderChildren.push(xml("file-name", {}, file.name))
        }
      }
    }
    // Add the file URL if available

    if (deliveryOrderId) {
      deliveryOrderChildren.push(xml("delivery-order-id", {}, deliveryOrderId))
    }

    if (originalMetadata?.price_netto_eur) {
      deliveryOrderChildren.push(
        xml("price-netto-eur", {}, originalMetadata.price_netto_eur.toString()),
      )
    }

    messageElements.push(
      xml(
        "delivery-order",
        "urn:slickshift:delivery-order:0",
        ...deliveryOrderChildren,
      ),
    )
  }

  return xml(
    "message",
    { type: "groupchat", to, from: window.myJID.toString(), id: uuid() },
    ...messageElements,
  )
}

const buildReplyGroupchatStanza = (
  to: string,
  text: string,
  replyMessage: Message,
  options: MessageOptions = {},
  original?: OriginalText,
) => {
  const replyFallback = `> ${MessageHelpers.userName(replyMessage)} wrote:\n${replyMessage.body
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n")}\n`

  const messageElements = [
    xml("body", {}, `${replyFallback}${text}`),
    xml("markable", "urn:xmpp:chat-markers:0"),
    xml("reply", {
      xmlns: "urn:xmpp:reply:0",
      to: replyMessage.from,
      id: replyMessage.id,
    }),
    xml(
      "fallback",
      { xmlns: "urn:xmpp:fallback:0", for: "urn:xmpp:reply:0" },
      xml("body", { start: 0, end: replyFallback.length }),
    ),
  ]

  if (original) {
    // The quote prefix is not translated, so it belongs on both versions and
    // the urn:xmpp:fallback:0 offsets stay valid for each.
    messageElements.push(
      originalElement({
        text: `${replyFallback}${original.text}`,
        lang: original.lang,
      }),
    )
  }

  if (options.skipRouteDetection) {
    messageElements.push(xml("skip-route-detection", "urn:slickshift:hints:0"))
  }

  if (options.startTracking) {
    window.isRouteStartTrackingCountdownActive = true
    document.dispatchEvent(new CustomEvent("start-tracking"))
  }

  if (options.deliveryOrderMetadata) {
    const { originalMetadata, files } = options.deliveryOrderMetadata
    const deliveryOrderChildren = []

    // Add the original metadata if available
    if (originalMetadata) {
      deliveryOrderChildren.push(
        xml("metadata", {}, JSON.stringify(originalMetadata)),
      )
    }

    // Add the file URL if available
    if (files) {
      for (const file of files) {
        if (file.url) {
          deliveryOrderChildren.push(xml("file-url", {}, file.url))
        }

        if (file) {
          deliveryOrderChildren.push(xml("file-name", {}, file.name))
        }
      }
    }

    messageElements.push(
      xml(
        "delivery-order",
        "urn:slickshift:delivery-order:0",
        ...deliveryOrderChildren,
      ),
    )
  }

  return xml(
    "message",
    { type: "groupchat", to, from: window.myJID.toString(), id: uuid() },
    ...messageElements,
  )
}

const buildForwardedGroupchatStanza = (
  to: string,
  originalMessage: Message,
): Element => {
  const message = originalMessage.forwarded
    ? originalMessage.forwarded
    : originalMessage
  // Create forwarded message fallback text (WhatsApp style with author)
  const fallbackBody = message.file
    ? `${message.fileName || "[Media]"}`
    : `${message.body}`

  const messageElements = [
    xml("body", {}, fallbackBody),
    xml("markable", "urn:xmpp:chat-markers:0"),
    xml("skip-route-detection", "urn:slickshift:hints:0"),
  ]

  // Add file sharing information to outer message for WhatsApp gateway and other systems
  if (message.file && message.url && message.fileName) {
    // XEP-0447 Stateless File Sharing - outer level
    messageElements.push(
      xml(
        "file-sharing",
        { xmlns: "urn:xmpp:sfs:0", disposition: "inline" },
        xml(
          "sources",
          {},
          xml("url-data", {
            xmlns: "http://jabber.org/protocol/url-data",
            target: message.url,
          }),
        ),
        xml(
          "file",
          "urn:xmpp:file:metadata:0",
          ...(message.fileName ? [xml("name", {}, message.fileName)] : []),
        ),
      ),
    )

    // XEP-0385 SIMS (Stateless Inline Media Sharing) - outer level
    const fileElements = []
    if (message.fileName) {
      fileElements.push(xml("name", {}, message.fileName))
    }
    if (message.thumbnail) {
      fileElements.push(
        xml("thumbnail", {
          width: message.thumbnail.width.toString(),
          height: message.thumbnail.height.toString(),
          uri: message.thumbnail.uri,
        }),
      )
    }

    const sourcesElements = []
    if (message.url) {
      sourcesElements.push(xml("reference", { uri: message.url }))
    }

    messageElements.push(
      xml(
        "reference",
        { xmlns: "urn:xmpp:reference:0" },
        xml(
          "media-sharing",
          { xmlns: "urn:xmpp:sims:1" },
          xml("file", {}, ...fileElements),
          xml("sources", {}, ...sourcesElements),
        ),
      ),
    )

    // Add store element for file persistence
    messageElements.push(xml("store", "urn:xmpp:hints"))

    // XEP-0066 Out of Band Data - for WhatsApp gateway
    messageElements.push(xml("x", "jabber:x:oob", xml("url", {}, message.url)))
  }

  // Build the inner message using appropriate builder
  let innerMessageStanza: Element

  if (message.file && message.url && message.fileName) {
    // Build file message using XmppSimsApi structure
    innerMessageStanza = buildFileMessageStanza(message)
  } else {
    // Build plain text message using existing function
    innerMessageStanza = buildPlainGroupchatStanza(message.to, message.body)
    // Update the attributes to match the original message
    innerMessageStanza.attrs.from = message.from
    innerMessageStanza.attrs.id = message.id
    innerMessageStanza.attrs.type = message.type
  }

  messageElements.push(
    xml(
      "forwarded",
      {
        xmlns: "urn:xmpp:forward:0",
      },
      xml("delay", {
        xmlns: "urn:xmpp:delay",
        stamp: message.timestamp,
        from: message.from,
      }),
      innerMessageStanza,
    ),
  )

  return xml(
    "message",
    { type: "groupchat", to, from: window.myJID.toString(), id: uuid() },
    ...messageElements,
  )
}

const buildFileMessageStanza = (originalMessage: Message): Element => {
  // Build file message stanza similar to XmppSimsApi.buildMessage but using parsed message data
  const messageElements = []

  // Add file-sharing element (similar to XmppSimsApi structure)
  if (originalMessage.url) {
    messageElements.push(
      xml(
        "file-sharing",
        { xmlns: "urn:xmpp:sfs:0", disposition: "inline" },
        xml(
          "sources",
          {},
          xml("url-data", {
            xmlns: "http://jabber.org/protocol/url-data",
            target: originalMessage.url,
          }),
        ),
        xml(
          "file",
          "urn:xmpp:file:metadata:0",
          // We don't have media-type or size from parsed message, so we skip them
          ...(originalMessage.fileName
            ? [xml("name", {}, originalMessage.fileName)]
            : []),
        ),
      ),
    )
  }

  // Add reference element (the main structure that messageParser expects)
  if (originalMessage.url || originalMessage.fileName) {
    const fileElements = []
    if (originalMessage.fileName) {
      fileElements.push(xml("name", {}, originalMessage.fileName))
    }
    if (originalMessage.thumbnail) {
      fileElements.push(
        xml("thumbnail", {
          width: originalMessage.thumbnail.width.toString(),
          height: originalMessage.thumbnail.height.toString(),
          uri: originalMessage.thumbnail.uri,
        }),
      )
    }

    const sourcesElements = []
    if (originalMessage.url) {
      sourcesElements.push(xml("reference", { uri: originalMessage.url }))
    }

    messageElements.push(
      xml(
        "reference",
        { xmlns: "urn:xmpp:reference:0" },
        xml(
          "media-sharing",
          { xmlns: "urn:xmpp:sims:1" },
          xml("file", {}, ...fileElements),
          xml("sources", {}, ...sourcesElements),
        ),
      ),
    )
  }

  // Add store and markable elements like XmppSimsApi
  messageElements.push(
    xml("store", "urn:xmpp:hints"),
    xml("markable", "urn:xmpp:chat-markers:0"),
  )

  // Add x element for out-of-band data
  if (originalMessage.url) {
    messageElements.push(
      xml("x", "jabber:x:oob", xml("url", {}, originalMessage.url)),
    )
  }

  // Add body element
  const bodyText =
    originalMessage.body ||
    originalMessage.fileName ||
    originalMessage.url ||
    "[Media]"
  messageElements.push(xml("body", {}, bodyText))

  return xml(
    "message",
    {
      from: originalMessage.from,
      id: originalMessage.id,
      type: originalMessage.type,
    },
    ...messageElements,
  )
}

const generateIncomingMessageBasedOnOutgoing = (
  message: Message | Reaction,
  myJID: JID,
): any => {
  const newMessage = { ...message }
  const toJid = jidParse(message.to)
  newMessage.from = `${toJid.bare()}/${myJID.local}`
  newMessage.senderJid = window.mucPlugin?.occupantJid(newMessage.from)
  newMessage.to = myJID.toString()

  return message.displayType === MessageDisplayType.Reaction
    ? { ...newMessage, localEcho: true }
    : newMessage
}

const parseToMessage = (stanza: Element) => {
  const message = ReactionsParser.parse(stanza)
  if (message) return message

  return MessageParser.parse(stanza, { toBeSent: true })
}

export const sendGroupchatStanza = (
  client: Client,
  dispatch: AppDispatch,
  stanza: Element,
  getState: () => RootState,
) => {
  // Adopt peeked chat: when sending to a peeked chat, convert it to a normal
  // chat by clearing the peek flag immediately and writing a bookmark in the
  // background. The send does NOT wait on the bookmark — the reply is what the
  // user asked for, the bookmark is bookkeeping.
  const chatJid = stanza.attrs.to
  if (chatJid) {
    const state = getState()
    const chat = state.chats.find((c) => c.jid === chatJid)
    if (chat?.peek) {
      // Clear the peek flag immediately
      dispatch(
        upsertChat({
          chat: { jid: chatJid, name: chat.name, room: chat.room, peek: false },
        }),
      )
      // Write bookmark in the background — don't await
      addBookmark(client, chatJid, chat.name).catch((error) => {
        // Bookmark write failed but message still goes through — the chat
        // will be dropped on next refresh, but the reply was already sent.
        captureError(error, {
          origin: "Other",
          extra: { context: "Failed to write bookmark for adopted chat" },
        })
      })
    }
  }

  const originId = uuid()
  const id = stanza.attrs.id
  stanza.append(xml("origin-id", { xmlns: "urn:xmpp:sid:0", id: originId }))

  window.analytics.track("GroupChatSendClick", {
    id,
    originId,
    stanza: stanza.toString(),
  })
  const message = parseToMessage(stanza)
  const fakedIncomingMessage = generateIncomingMessageBasedOnOutgoing(
    message,
    window.myJID,
  )
  const myJid = window.myJID.bare().toString()

  if (message.displayType === MessageDisplayType.Message) {
    dispatch(
      addMessage({
        message: fakedIncomingMessage,
        myJid: myJid,
      }),
    )
  } else {
    dispatch(
      addBatchMessageReactions({
        reactions: [fakedIncomingMessage],
        myJid: myJid,
      }),
    )
  }

  dispatch(
    addToSendoutQueue({
      messageId: message.id,
      stanza: stanza.toString(),
      messageType: "groupchat",
    }),
  )
  window.persistor.flush()
  window.sendoutQueueWorker.send(message.id)
}

/** The text a desktop toast would show for `message`; undefined when blank. */
const desktopNotificationBody = (
  message: Message,
  state: RootState,
): string | undefined => {
  let roomJid: string | undefined
  try {
    roomJid = jid.parse(message.from).bare().toString()
  } catch {
    roomJid = undefined
  }
  const accountChat = roomJid
    ? selectAccountChatByJid(roomJid)(state)
    : undefined
  const body =
    getTransicsMessagePresentation(message, accountChat)?.body ?? message.body
  return typeof body === "string" && body.trim().length > 0 ? body : undefined
}

const showDesktopNotificationIfNeeded = (
  message: Message,
  myJid: string,
  dispatch: AppDispatch,
  state: RootState,
) => {
  if (window.Notification && window.Notification.permission === "granted") {
    const chatIndexForNotification = ChatHelpers.findMessageChatIndex(
      message,
      state.chats,
      myJid,
    )
    const currentlyActiveChatIndex = selectActiveChatIndex(state)

    const chatJid = state.chats[chatIndexForNotification].jid

    const shouldNotify = selectShouldNotify(chatJid)(state)

    if (!shouldNotify) return

    if (isMessageFromTranschat(message.from)) return

    // The dispatcher↔agent conversation lives in the agent column. A toast
    // titled with the driver room would put it back in front of the office.
    if (isAgentChatMessage(message)) return

    if (isSilencedSlickAIMessage(message, state)) return

    const { notifyOnlyOnDriverMessages } = selectSettings(state)
    if (notifyOnlyOnDriverMessages) {
      const fromJid = jid.parse(message.from)
      const roomBareJid = fromJid.bare().toString()
      if (isDriverRoom(roomBareJid, state)) {
        if (MessageHelpers.isFromForwarder(message)) return
        const chat = state.chats[chatIndexForNotification]
        const roomDomain = fromJid.domain.split(".").slice(1).join(".")
        const senderBareJid = MessageHelpers.fromBareJid(message, roomDomain)
        const sender = chat.participants.find((p) => p.jid === senderBareJid)
        if (sender?.role !== "driver") return
      }
    }

    // The message is already on screen, so the toast would be noise — unless
    // the account asked for it (see `lib/notifyOnActiveChat.ts`).
    const isReadingThisChat =
      chatIndexForNotification === currentlyActiveChatIndex &&
      window.document.visibilityState === "visible" &&
      window.document.hasFocus()
    if (isReadingThisChat && !isNotifyOnActiveChatEnabled()) return

    // Machine messages carry only a payload: the CMR automation's extracted
    // metadata and driver context, reactions, markers. A toast with a name and
    // no text tells the dispatcher nothing, so there is none.
    const notificationBody = desktopNotificationBody(message, state)
    if (!notificationBody) return

    try {
      const notification = new Notification(
        messageAuthorName(message, jid.parse(myJid))(state),
        {
          body: notificationBody,
          icon: `/slickshift-large.png`,
        },
      )
      notification.onclick = () => {
        window.focus()
        notification.close()
        dispatch(
          changeActiveChat({
            activeChatJid: jid.parse(message.from).bare().toString(),
          }),
        )
      }
      playNotificationSound()
    } catch (e: any) {
      console.error(e)
    }
  }
}

export const processIncomingMessage = (
  messageAction: AddMessageAction,
  client: Client,
  stanzaType: StanzaType,
) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    if (!client.jid)
      throw "Can not add a new message, jid is undefined on client instance"

    // if message is a correction
    // no need to send markers / notifications
    if (messageAction.message.replaceId) {
      dispatch(addMessageCorrection(messageAction))
      return
    }

    dispatch(addMessage(messageAction))

    if (MessageHelpers.isToMyself(messageAction.message)) return

    if (
      stanzaType === "message" &&
      MessageHelpers.isIncoming(messageAction.message) &&
      !MessageHelpers.isToMyself(messageAction.message)
    ) {
      showDesktopNotificationIfNeeded(
        messageAction.message,
        messageAction.myJid,
        dispatch,
        getState(),
      )

      // A peek is silent: no read marker goes into someone else's room.
      if (!isMessageInPeekedChat(getState(), messageAction.message)) {
        const markerToJid =
          messageAction.message.type === "chat"
            ? messageAction.message.from.toString()
            : jid.parse(messageAction.message.from).bare().toString()

        ChatMarkersApi.sendMarker(
          "received",
          messageAction.message.id,
          markerToJid,
          messageAction.message.type,
          client,
        ).then((markerStanza) => {
          if (messageAction.message.type === "chat" && client.jid) {
            const parsedMarker = MarkerParser.parse(markerStanza)

            dispatch(
              markMessage({
                marker: parsedMarker,
                myJid: client.jid?.toString(),
              }),
            )
          }
        })
      }
    }
  }
}

export const joinChat = (
  _: Client,
  chat: Pick<Chat, "jid" | "name" | "room">,
): AppThunk => {
  return async (dispatch, getState) => {
    const chatInStore = ChatHelpers.isChatInStore(getState(), chat.jid)

    dispatch(upsertChat({ chat: { ...chat, peek: false } }))

    if (chatInStore) {
      return
    }

    dispatch(addToSyncQueue({ jid: chat.jid }))
  }
}

export const leaveChat = (client: Client, chat: Chat): AppThunk => {
  return async (dispatch, getState) => {
    if (!ChatHelpers.isChatInStore(getState(), chat.jid)) return

    dispatch(removeChat({ jid: chat.jid }))
    XmppGroupChatApi.sendRoomExitPresence(
      client,
      chat.jid,
      client.jid!.getLocal(),
    )
  }
}

/** Open a chat that belongs to another dispatcher: join the room so history
 * loads and replies can be sent, but leave no bookmark behind. */
export const peekChat = (
  _: Client,
  chat: { jid: string; name: string },
): AppThunk => {
  return async (dispatch, getState) => {
    const alreadyHave = ChatHelpers.isChatInStore(getState(), chat.jid)
    dispatch(upsertChat({ chat: { ...chat, room: true, peek: !alreadyHave } }))
    if (alreadyHave) return
    dispatch(addToSyncQueue({ jid: chat.jid }))
  }
}

/** Drop every peeked chat, optionally keeping the one just opened. */
export const leavePeekedChats = (
  client: Client,
  keepJid?: string,
): AppThunk => {
  return async (dispatch, getState) => {
    const peeked = getState().chats.filter(
      (chat) => chat.peek && chat.jid !== keepJid,
    )
    for (const chat of peeked) {
      dispatch(leaveChat(client, chat))
    }
  }
}

/** Returns true if the message is in a peeked chat. Peeked chats are silent:
 * no read markers are sent. */
const isMessageInPeekedChat = (
  state: RootState,
  message: Pick<Message, "from" | "type">,
): boolean => {
  const chatJid =
    message.type === "groupchat"
      ? jid.parse(message.from)?.bare().toString()
      : message.from
  return state.chats.some((chat) => chat.jid === chatJid && chat.peek)
}

export const markMessageDisplayed = (client: Client, message: Message) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const myJid = client.jid?.toString()
    if (!myJid) return

    // A peek is silent: no read marker goes into someone else's room.
    if (isMessageInPeekedChat(getState(), message)) {
      return
    }

    const chatJid =
      message.type === "groupchat"
        ? jid.parse(message.from)?.bare().toString()
        : message.from

    ChatMarkersApi.sendMarker(
      "displayed",
      message.id,
      chatJid,
      message.type,
      client,
    ).then((marker) => {
      const sendStatus = message.type === "chat" ? "sent" : "sending"
      const parsedMarker = MarkerParser.parse(marker, { sendStatus })

      dispatch(
        markMessage({
          marker: parsedMarker,
          myJid: myJid.toString(),
        }),
      )
    })
  }
}

export const markAllChatsAsRead = (client: Client) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state: RootState = getState()

    const myJid = client.jid?.toString()

    if (!myJid) return

    state.chats.forEach((chat) => {
      if (
        ChatHelpers.unreadMessagesCount(
          { ...chat, ...state.messages[chat.jid] },
          myJid,
        ) > 0
      ) {
        const message = state.messages[chat.jid].messages.at(-1)
        if (message) {
          dispatch(markMessageDisplayed(client, message))
        }
      }
    })
  }
}

export const updateDriverProfile = (client: Client, driverJid: string) => {
  return async (dispatch: AppDispatch) => {
    const vcard = await XmppVCardApi.fetchVCard(client, driverJid)
    if (!vcard) return
    dispatch(upsertProfile({ profile: vcard }))
    setTimeout(() => {
      window.persistor.flush()
    }, 1000)
  }
}

export const changeActiveChatToFirstNotArchivedDriver = (chatJid: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state: RootState = getState()

    const sortedChats = sortChats(
      state.chats,
      state.messages,
      selectIsSlickAISilenced(state),
    )
    const archivedChats = [...selectArchivedChats(state), chatJid]

    const nextDriverChat = sortedChats.find(
      (chat) =>
        !archivedChats.includes(chat.jid) &&
        chat.participants.some((p) => p.role === "driver"),
    )

    // No other non-archived driver chat to switch to: stay on the chat just
    // archived rather than falling back to an unrelated chat (a group room,
    // the Slick AI thread...) that has nothing to do with what the dispatcher
    // was looking at. `chatJid` is always a chat already in the store - it's
    // the one whose header this was dispatched from.
    dispatch(
      changeActiveChat({ activeChatJid: nextDriverChat?.jid ?? chatJid }),
    )
  }
}
